import React, { FC, useCallback, useContext, useMemo } from 'react';

import { useRouter } from 'expo-router';
import { useStoreContext } from 'features/store';
import { GestureResponderEvent } from 'react-native';

import CtaButton from 'components/top-service-mode/actions/cta-button';
import { ServiceMode } from 'generated/graphql-gateway';
import { useDistanceText } from 'hooks/geolocation/use-distance-text';
import { useIsDesktopBreakpoint } from 'hooks/use-media-query';
import { getStaticPathname } from 'navigation/location/location';
import { appendObjectToQueryString } from 'navigation/utils';
import { CustomEventNames, EventTypes, logEvent } from 'state/amplitude';
import { t } from 'state/intl/translations';
import { useServiceModeLive } from 'state/service-mode';
import { SmartAlertsContext } from 'state/smart-alert';
import { getDesignToken } from 'styles/constants';
import { routes } from 'utils/routing';

import ChooseLocation from './actions/choose-location';
import {
  ArrowRightIcon,
  ButtonWrapper,
  ClickableWrapper,
  Container,
  Details,
  DetailsContainer,
  DetailsWrapper,
  Heading,
  IconContainer,
  IconStyled,
  SmartAlertText,
} from './styled';
import { ITopServiceModeProps } from './types';

// Maximum allowed number of characters for the selected location address as per DS
const maxChars = 24;

/**
 * Abstraction point for top service UI/feature roll out of future ordering
 */
export const TopServiceModeView: FC<React.PropsWithChildren<ITopServiceModeProps>> = ({
  heading,
  details,
  icon,
  rbiOrderId,
  isInCart = false,
}) => {
  const { noStoreSelected, store: restaurant } = useStoreContext();
  const serviceMode = useServiceModeLive();
  const isDelivery = serviceMode === ServiceMode.DELIVERY;
  const router = useRouter();
  const isDesktop = useIsDesktopBreakpoint();
  const { distanceText, isLoading, canShowDistance } = useDistanceText({
    restaurant,
    shouldUseUserCoordinatesIfAvailable: true,
  });
  const { tsmSmartAlert } = useContext(SmartAlertsContext);

  const customDetails = useMemo(
    () =>
      typeof details !== 'string' || details.length <= maxChars || isDesktop
        ? details
        : `${details.substring(0, maxChars)}...`,
    [details, isDesktop]
  );

  const handleServiceModeChange = useCallback(() => {
    logEvent(CustomEventNames.TOP_SERVICE_MODE, EventTypes.Other, {
      'Service Mode': serviceMode,
    });

    // this preserves the functionality of dismiss delivery address and you see the store locator screen
    isDelivery && router.push(routes.storeLocator);

    const isInCart = getStaticPathname() === routes.cart;
    const navigateTo = isDelivery ? routes.deliveryAddress : routes.storeLocator;

    router.navigate({
      pathname: navigateTo,
      ...(isInCart && { params: { redirectUrl: routes.cart } }),
    });
  }, [serviceMode, isDelivery, router]);

  const handleNavigateToOrderConfirmation = (rbiOrderId: string, showToast?: boolean) => {
    logEvent(CustomEventNames.TOP_SERVICE_MODE, EventTypes.Other, {
      'Service Mode': serviceMode,
      'Order Id': rbiOrderId,
    });
    router.navigate(
      appendObjectToQueryString(`${routes.orderConfirmation}/${rbiOrderId}`, { showToast })
    );
  };

  const getCtaButton = () => {
    if (rbiOrderId && !isInCart) {
      return (
        <CtaButton
          text={t('trackOrder')}
          cta={() => handleNavigateToOrderConfirmation(rbiOrderId)}
        />
      );
    } else if (serviceMode === ServiceMode.DELIVERY) {
      return (
        <CtaButton
          text={t('change')}
          isLoading={isLoading}
          cta={handleServiceModeChange}
          icon={<ArrowRightIcon />}
        />
      );
    }

    if (canShowDistance) {
      return (
        <CtaButton
          text={distanceText}
          isLoading={isLoading}
          cta={handleServiceModeChange}
          icon={<ArrowRightIcon />}
          showDistanceText
          bold
          accessibilityLabel={t('changeStores')}
        />
      );
    }

    return <CtaButton cta={handleServiceModeChange} icon={<ArrowRightIcon />} />;
  };

  const handleSmartAlertPress = (e: GestureResponderEvent) => {
    e.stopPropagation();
    if (!tsmSmartAlert?.linkUrl) {
      return;
    }
    router.navigate(tsmSmartAlert?.linkUrl);
  };

  const button = getCtaButton();

  return (
    <Container testID="top-service-mode">
      <ClickableWrapper
        dd-action-name="top-service-mode-wrapper"
        testID="top-service-mode-wrapper"
        onPress={handleServiceModeChange}
        accessibilityRole="link"
        hasAlert={!!tsmSmartAlert}
      >
        <IconContainer>
          <IconStyled color={getDesignToken('icon-default')} variant={icon} />
        </IconContainer>

        <DetailsWrapper>
          <DetailsContainer justifyContent={noStoreSelected ? 'space-between' : null}>
            <Heading>{heading}</Heading>
            {details ? (
              <Details accessibilityLabel={`${t('address')}: ${details}`}>
                {isInCart ? details : customDetails}
              </Details>
            ) : (
              <ChooseLocation testID="top-service-choose-location" />
            )}
          </DetailsContainer>
          {!!tsmSmartAlert?.message && (
            <SmartAlertText onPress={handleSmartAlertPress}>{tsmSmartAlert.message}</SmartAlertText>
          )}
        </DetailsWrapper>
      </ClickableWrapper>
      {button && !noStoreSelected && <ButtonWrapper>{button}</ButtonWrapper>}
    </Container>
  );
};
